import { useState } from 'react';
import { Group, Select, Table, Text } from '@mantine/core';
import type { AppUserSummary, RoleScopeType, RoleType } from '@isg/shared-types';
import { izinliBahceTurleri, sapkaSinifi, ELLE_VERILEBILEN_SAPKALAR } from '@isg/validation';
import {
  useBahceCikar,
  useBahceEkle,
  useCompanies,
  useContractors,
  useLocationsTumu,
  useSapkaGeriAl,
  useSapkaVer,
  useWorkplacesTumu,
  useSahaGunu,
} from '../api/queries';
import { KAPSAM_TIP_ETIKET, SAPKA_ETIKET } from '../i18n/tr';
import { tarihGoster } from '../lib/format';
import { Rozet, SatirEylemleri, SolukSatir } from '../sozlesme';
import { ANLAM } from '../theme';
import { BosListeNotu } from './parts';

/**
 * ŞAPKA + BAHÇE YÜZEYİ — **İKİ EKRANIN PAYLAŞTIĞI TEK BİLEŞEN** (F4·B2,
 * karar **Y1**: Kapsam §20.7, `.claude/rules/yetki.md`).
 *
 * > **Yetki kişiye asılır; ikinci ekran aynı kapıyı çalar.**
 *
 * Kişi kartı (ASIL yüzey) ve **Kullanıcılar** ekranı bu dosyayı kullanır.
 * ⚠️ **Yazma yolu YALNIZ buradadır** — `useSapkaVer` / `useSapkaGeriAl` /
 * `useBahceEkle` / `useBahceCikar` başka hiçbir ekrandan çağrılmaz ve bu
 * `yetki-parts.spec.ts` kod denetimiyle kilitlidir. İkinci bir yol, ikinci
 * doğrulama kümesi ve zamanla ayrışan iki davranış demektir (emsal:
 * §18.1/P1 — *eşleştirme kanal başına yazılmaz*).
 *
 * ⚠️ **BAHÇE AĞACININ DERİNLİĞİ `izinliBahceTurleri`DEN OKUNUR** (Y2 —
 * B1'in şema kısıtı). Ekran ikinci bir liste TUTMAZ: kapsama şapkasında
 * seçim **işyeri düzeyinde durur**, alan sahipliğinde (`area_supervisor`)
 * **dallar açılır**. Kapı yine de sunucudadır (422) — ekran onu ÖNLER,
 * yerine geçmez.
 *
 * ⚠️ **SÖZLEŞME (P1/P2):** bahçe satırları **alt kalemdir** → satır içi
 * girilir (soluk satır), kendi sayfası açılmaz. Satırda bir birincil eylem;
 * **kaldırma ⋮ altındadır** (P2-a — geri alınamaz eylem satırda durmaz).
 */

/**
 * Şapka seçenekleri — `employee` DE listede (temel görünüm bir şapkadır).
 * ✏️ 5½ / B1 (Kapsam §25/13): `employee_rep` LİSTEDE DEĞİL — çalışan
 * temsilciliği GÖREVLENDİRMEDEN türetilir; elle atama yolu kapandı (sunucu
 * 422). Çalışamayacak kontrol çizilmez (§2.40).
 */
const SAPKA_SECENEKLERI = ELLE_VERILEBILEN_SAPKALAR.map((sapka: RoleType) => ({
  value: sapka,
  label: SAPKA_ETIKET[sapka],
}));

/**
 * Bahçesi boş şapka HİÇBİR ŞEY görmez (fail-closed, §20.2/B3) — **rol
 * ekranının DENETİM işi budur.** Kural tek kaynaktan okunur: `bahcesiz`
 * sınıf bahçe kaydı ALMAZ (kapsamı holding geneli), diğerlerinde boş bahçe
 * bir arızadır.
 */
export function bahcesiBosMu(atama: AppUserSummary['atamalar'][number]): boolean {
  return sapkaSinifi(atama.sapka) !== 'bahcesiz' && atama.scopes.length === 0;
}

/**
 * ✏️ **TUR 9 / B6 — ESKİYEN ELLE SATIR.** Bir yıldan eski bahçe kaydı
 * işaretlenir. ⚠️ **Eşik bir KURAL DEĞİL, bir GÖRÜNÜRLÜK ölçüsüdür:** hiçbir
 * kapıyı kapatmaz, hiçbir satırı düşürmez — vekalet mekanizması hâlâ YOKTUR
 * (Kapsam §20.4 aynen). Sunucuya taşımadık çünkü sunucuda bir karar
 * üretmiyor; ekranda bir soru sorduruyor: *"bu hâlâ geçerli mi?"*
 */
function eskiMi(eklendiAt: string, sahaGunu: string | undefined): boolean {
  // ⚠️ **METİN KESİLMEZ** (G11 kod denetimi): ISO damgasının gün parçasını
  // kesmek bir BİÇİMLENDİRMEDİR ve tek kaynağı `lib/format.ts`tir — kod
  // denetimi o deseni bu dosyada **yorumda bile** yakalar (ilk yazımda tam
  // olarak öyle yakalandı). Burada yapılan iş biçimlendirme değil
  // KARŞILAŞTIRMADIR → gün metni doğrudan `Date`e çevrilir (ISO gün dizgesi
  // UTC gece yarısı okunur; bir yıllık eşikte saat dilimi kayması sonucu
  // değiştirmez).
  // Madde 302 (08.10.2026): "bugün" tarayıcıdan değil SAHA GÜNÜNDEN (sunucu);
  // gün gelmeden rozet çizilmez.
  if (!sahaGunu) return false;
  const birYilOnce = new Date(`${sahaGunu}T00:00:00.000Z`);
  birYilOnce.setUTCFullYear(birYilOnce.getUTCFullYear() - 1);
  return new Date(eklendiAt).getTime() < birYilOnce.getTime();
}

/** Bir kullanıcının şapka/bahçe satırlarının ortak gövdesi. */
export function SapkaBahceDuzenleyici({
  kullanici,
  yazabilir,
  testIdOnek = 'yetki',
}: {
  kullanici: AppUserSummary;
  /** Şapka VERME/GERİ ALMA yetkisi (yalnız holding yöneticisi — §20.5/A4). */
  yazabilir: boolean;
  testIdOnek?: string;
}): React.JSX.Element {
  const sapkaVer = useSapkaVer();
  const sapkaGeriAl = useSapkaGeriAl();
  const [yeniSapka, setYeniSapka] = useState<string | null>(null);

  return (
    <div data-testid={`${testIdOnek}-duzenleyici`}>
      {kullanici.atamalar.length === 0 && !yazabilir && (
        <BosListeNotu mesaj="Şapka verilmemiş — bu kullanıcı temel görünümde (çalışan)." />
      )}
      {kullanici.atamalar.map((atama) => (
        <div key={atama.id} data-testid={`${testIdOnek}-atama`} style={{ marginBottom: 12 }}>
          <Group gap="xs" wrap="nowrap">
            <Text fw={600} size="sm" data-testid={`${testIdOnek}-sapka`}>
              {SAPKA_ETIKET[atama.sapka]}
            </Text>
            {bahcesiBosMu(atama) && (
              <Rozet anlam="engel" testId={`${testIdOnek}-bos-bahce`}>
                Bahçesi boş — hiçbir şey göremez
              </Rozet>
            )}
            {sapkaSinifi(atama.sapka) === 'bahcesiz' && (
              <Rozet anlam="bilgi" testId={`${testIdOnek}-bahcesiz`}>
                Kapsamı holding geneli
              </Rozet>
            )}
            {yazabilir && (
              <SatirEylemleri
                testId={`${testIdOnek}-sapka-eylemleri`}
                digerleri={[
                  {
                    etiket: 'Şapkayı geri al',
                    tehlikeli: true,
                    testId: `${testIdOnek}-sapka-geri-al`,
                    onClick: () => sapkaGeriAl.mutate({ assignmentId: atama.id }),
                  },
                ]}
              />
            )}
          </Group>
          {/* ✏️ GEZİNTİ ARTÇISI 3 / B5 — ŞAPKA DEĞİŞTİRME İKİ ADIMLIDIR VE BUNU
              EKRAN SÖYLER. Model B (tek görev şapkası): ikinci şapka verilemez,
              önce geri alınır. Mekanizma doğruydu, keşfedilemiyordu (§2.40'ın
              "görünür, keşfedilemez" kolu). M1 istisnası: karar anında bilgi —
              yalnız YAZABİLENDE çizilir (yazamayana kısıt cümlesi gürültüdür). */}
          {yazabilir && (
            <Text size="xs" c="dimmed" mt={4} data-testid={`${testIdOnek}-sapka-degistirme-notu`}>
              Şapka değiştirmek için önce geri alın; sonra yeni şapka seçilir.
            </Text>
          )}
          <BahceTablosu
            atama={atama}
            turetilenIsyerleri={kullanici.turetilenIsyerleri}
            testIdOnek={testIdOnek}
          />
        </div>
      ))}

      {yazabilir && kullanici.atamalar.length === 0 && (
        <Group gap="xs" align="flex-end" data-testid={`${testIdOnek}-sapka-ver`}>
          <Select
            searchable
            size="sm"
            placeholder="Şapka seç"
            aria-label="Şapka"
            data-testid={`${testIdOnek}-sapka-secim`}
            value={yeniSapka}
            onChange={setYeniSapka}
            data={SAPKA_SECENEKLERI}
          />
          <SatirEylemleri
            birincil={{
              etiket: 'Şapkayı ver',
              pasif: yeniSapka === null || sapkaVer.isPending,
              testId: `${testIdOnek}-sapka-kaydet`,
              onClick: () => {
                if (!yeniSapka) return;
                sapkaVer.mutate(
                  { appUserId: kullanici.id, sapka: yeniSapka as RoleType },
                  { onSuccess: () => setYeniSapka(null) },
                );
              },
            }}
          />
        </Group>
      )}
      {(sapkaVer.isError || sapkaGeriAl.isError) && (
        <Text size="xs" c={ANLAM.engel} role="alert" mt="xs">
          Şapka kaydedilemedi: {(sapkaVer.error ?? sapkaGeriAl.error)?.message}
        </Text>
      )}
    </div>
  );
}

/**
 * Bahçe satırları + soluk satır. Bahçe ekle/çıkar holding yöneticisi VE
 * şirket yöneticisindedir (kendi bahçesinde) — bu yüzden şapka yazma
 * yetkisinden AYRI çizilir; aşan hedefin kapısı sunucudadır (403).
 */
function BahceTablosu({
  atama,
  turetilenIsyerleri,
  testIdOnek,
}: {
  atama: AppUserSummary['atamalar'][number];
  /** TUR 9 / K6 — kişinin istihdamından TÜRETİLEN taban (soluk, salt okunur). */
  turetilenIsyerleri: AppUserSummary['turetilenIsyerleri'];
  testIdOnek: string;
}): React.JSX.Element | null {
  // Madde 302: satır yaşı rozetinin "bugün"ü saha gününden (sunucu).
  const sahaGunu = useSahaGunu();
  const bahceEkle = useBahceEkle();
  const bahceCikar = useBahceCikar();
  const izinliTurler = izinliBahceTurleri(atama.sapka);
  const [tur, setTur] = useState<string | null>(null);
  const [hedef, setHedef] = useState<string | null>(null);

  const companies = useCompanies();
  // ✏️ TUR 9 / B4: şirket başına fan-out yerine TEK istek (T9-18).
  const isyerleri = useWorkplacesTumu().data ?? [];
  // ⚠️ Lokasyon ağacı YALNIZ dal alabilen şapkada çekilir: kapsama
  // şapkasında o düğümler seçilemez, sorgu da atılmaz.
  // ✏️ TUR 9 / B4 (T9-19): işyeri başına fan-out yerine TEK istek.
  const dalAlabilir = izinliTurler.includes('location_branch');
  const dugumler = useLocationsTumu({ enabled: dalAlabilir }).data ?? [];
  const contractors = useContractors({ enabled: izinliTurler.includes('contractor') });

  if (izinliTurler.length === 0) {
    // `bahcesiz` sınıf: bahçe kaydı ALMAZ (ekleme 422). Tablo hiç çizilmez.
    return null;
  }

  // ✏️ MADDE 232 (ürün sahibi 08.09.2026; kod 13.09.2026) — B4 REVİZYONU:
  // türetilen (istihdamdan gelen) işyeri ve dalları seçenekten ARTIK DÜŞMEZ.
  // 08.09'da "türetilen zaten kapsıyor, elle satır etkisiz" diye düşürülmüştü;
  // aynı gün ölçüm tersini gösterdi: elle satır ONAY ekseninde tek kaynaktır
  // (PTW onay havuzu yalnız elle bahçeyi okur) — orada çalışana kendi işyerini
  // elle vermek kurulum kılavuzunun ZORUNLU 3. adımıdır. Engel yerine
  // görünürlük: elle satır aşağıda anlamını söyleyen rozet taşır (R4).
  const hedefSecenekleri =
    tur === 'company'
      ? (companies.data ?? []).map((c) => ({ value: c.id, label: c.ad }))
      : tur === 'workplace'
        ? isyerleri.map((w) => ({ value: w.id, label: w.ad }))
        : tur === 'location_branch'
          ? dugumler.map((d) => ({ value: d.id, label: d.ad }))
          : tur === 'contractor'
            ? (contractors.data ?? []).map((f) => ({ value: f.id, label: f.ad }))
            : [];

  const dokunuldu = tur !== null || hedef !== null;
  const kaydedilebilir = tur !== null && hedef !== null && !bahceEkle.isPending;
  const kaydet = (): void => {
    if (!tur || !hedef) return;
    const kapsamTip = tur as RoleScopeType;
    bahceEkle.mutate(
      {
        assignmentId: atama.id,
        kapsamTip,
        companyId: kapsamTip === 'company' ? hedef : null,
        workplaceId: kapsamTip === 'workplace' ? hedef : null,
        locationNodeId: kapsamTip === 'location_branch' ? hedef : null,
        contractorId: kapsamTip === 'contractor' ? hedef : null,
      },
      {
        onSuccess: () => {
          setTur(null);
          setHedef(null);
        },
      },
    );
  };

  return (
    <>
      <Table striped data-testid={`${testIdOnek}-bahce-tablosu`}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Kapsam</Table.Th>
            <Table.Th>Yer</Table.Th>
            <Table.Th w={44} aria-label="Satır eylemi" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {/* ✏️ **TUR 9 / K6 — TÜRETİLEN SATIRLAR ÖNCE, SOLUK VE SALT
              OKUNUR.** Bahçenin TABANI kişinin kendi istihdamından doğar
              (`employment.workplace_id`) ve **bir `role_scope` satırı
              değildir**: kaldırılamaz. Göstermemek, kullanıcıya *"bu kişi
              burayı neden görüyor?"* sorusunu cevapsız bırakırdı; kaldırma
              düğmesi çizmek ise **basılınca geri gelen bir satır** üretirdi
              (§2.40'ın adayı). */}
          {turetilenIsyerleri.map((isyeri) => (
            <Table.Tr
              key={`turetilen-${isyeri.id}`}
              data-testid={`${testIdOnek}-bahce-turetilen`}
              style={{ opacity: 0.6 }}
            >
              <Table.Td>{KAPSAM_TIP_ETIKET.workplace}</Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <Text size="sm">{isyeri.ad}</Text>
                  <Rozet anlam="bilgi" testId={`${testIdOnek}-turetilen-rozet`}>
                    İstihdamdan
                  </Rozet>
                </Group>
              </Table.Td>
              <Table.Td />
            </Table.Tr>
          ))}
          {atama.scopes.map((scope) => (
            <Table.Tr key={scope.id} data-testid={`${testIdOnek}-bahce-satiri`}>
              <Table.Td>{KAPSAM_TIP_ETIKET[scope.kapsamTip]}</Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <Text size="sm">{scope.hedefAd}</Text>
                  {/* ✏️ **MADDE 232 / R4 — ELLE SATIR ANLAMINI SÖYLER.** Türetilen
                      satır "İstihdamdan" (OKUMA açar) rozetini taşır; elle satır
                      SORUMLULUK eksenidir — PTW onay havuzu yalnız bunu okur.
                      Aynı işyeri iki satırda iki anlam: mükerrer değil. Rozet
                      düzeyinde ayrım (M1 — açıklama cümlesi yazılmadı). */}
                  <Rozet anlam="bilgi" testId={`${testIdOnek}-elle-rozet`}>
                    Elle · onaylayabilir
                  </Rozet>
                  {/* ✏️ **TUR 9 / B6 (sürtünme 1/3) — SATIRIN YAŞI GÖRÜNÜR.**
                      Bahçe kaydı tarih taşımaz ve otomatik düşmez (Kapsam
                      §20.4); vekaleten verilmiş bir bahçe **unutulur**. Bu
                      rozet bir MEKANİZMA DEĞİLDİR — sessiz yetki sızıntısını
                      çözmez, GÖRÜNÜR kılar. */}
                  <Text size="xs" c="dimmed" data-testid={`${testIdOnek}-bahce-eklendi`}>
                    {tarihGoster(scope.eklendiAt)}
                  </Text>
                  {eskiMi(scope.eklendiAt, sahaGunu.data) && (
                    <Rozet anlam="dikkat" testId={`${testIdOnek}-bahce-eski`}>
                      Bir yıldan eski
                    </Rozet>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <SatirEylemleri
                  digerleri={[
                    {
                      etiket: 'Bahçeden çıkar',
                      tehlikeli: true,
                      testId: `${testIdOnek}-bahce-cikar`,
                      onClick: () =>
                        bahceCikar.mutate({ assignmentId: atama.id, scopeId: scope.id }),
                    },
                  ]}
                />
              </Table.Td>
            </Table.Tr>
          ))}
          <SolukSatir
            testId={`${testIdOnek}-bahce-soluk-satir`}
            dokunuldu={dokunuldu}
            kaydedilebilir={kaydedilebilir}
            kaydediliyor={bahceEkle.isPending}
            onKaydet={kaydet}
            kolonSayisi={2}
          >
            <Table.Td>
              <Select
                size="sm"
                placeholder="Kapsam"
                aria-label="Bahçe kapsamı"
                data-testid={`${testIdOnek}-bahce-tur`}
                value={tur}
                onChange={(deger) => {
                  setTur(deger);
                  setHedef(null);
                }}
                // ⚠️ DERİNLİK TEK KAYNAKTAN: kapsama şapkasında
                // `location_branch` bu listede HİÇ YOKTUR (Y2).
                data={izinliTurler.map((t) => ({ value: t, label: KAPSAM_TIP_ETIKET[t] }))}
              />
            </Table.Td>
            <Table.Td>
              <Select
                searchable
                size="sm"
                placeholder="Yer seç"
                aria-label="Bahçe hedefi"
                data-testid={`${testIdOnek}-bahce-hedef`}
                disabled={tur === null}
                value={hedef}
                onChange={setHedef}
                data={hedefSecenekleri}
              />
            </Table.Td>
          </SolukSatir>
        </Table.Tbody>
      </Table>
      {(bahceEkle.isError || bahceCikar.isError) && (
        <Text
          size="xs"
          c={ANLAM.engel}
          role="alert"
          mt="xs"
          data-testid={`${testIdOnek}-bahce-hata`}
        >
          Bahçe kaydedilemedi: {(bahceEkle.error ?? bahceCikar.error)?.message}
        </Text>
      )}
    </>
  );
}
